import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Alert,
  Typography,
  Collapse,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';

interface FormErrors {
  email?: string;
  password?: string;
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [licenseExpanded, setLicenseExpanded] = useState(false);

  // If already authenticated, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!email.trim()) {
      errors.email = 'メールアドレスを入力してください';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = '有効なメールアドレスを入力してください';
    }

    if (!password) {
      errors.password = 'パスワードを入力してください';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      await login(email, password);
      navigate('/', { replace: true });
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string; message?: string; lockedUntil?: string } } };
      const responseData = axiosError?.response?.data;

      if (responseData?.error === 'ACCOUNT_LOCKED') {
        const lockedUntil = responseData.lockedUntil;
        if (lockedUntil) {
          const lockedDate = new Date(lockedUntil);
          const now = new Date();
          const remainingMinutes = Math.max(1, Math.ceil((lockedDate.getTime() - now.getTime()) / (1000 * 60)));
          setApiError(`アカウントがロックされています。${remainingMinutes}分後に再試行してください`);
        } else {
          setApiError('アカウントがロックされています。30分後に再試行してください');
        }
      } else if (responseData?.error === 'INVALID_CREDENTIALS') {
        setApiError('メールアドレスまたはパスワードが正しくありません');
      } else {
        setApiError('ログインに失敗しました。しばらくしてから再試行してください');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: 'grey.100',
        py: 4,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 400 }}>
        <CardContent sx={{ p: 4 }}>
          {/* Logo + Title */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 4 }}>
            <img src="/favicon.svg" alt="" width={36} height={36} style={{ display: 'block' }} />
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 'bold', fontFamily: '"Meiryo", "メイリオ", sans-serif' }}
            >
              <span style={{ color: '#1B3A6B' }}>Cell</span>
              <span style={{ color: '#2E8B7A' }}>Box</span>
            </Typography>
          </Box>

          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              placeholder="メールアドレス"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (formErrors.email) {
                  setFormErrors((prev) => ({ ...prev, email: undefined }));
                }
              }}
              error={!!formErrors.email}
              helperText={formErrors.email}
              margin="normal"
              autoComplete="email"
              autoFocus
              disabled={submitting}
            />

            <TextField
              fullWidth
              placeholder="パスワード"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (formErrors.password) {
                  setFormErrors((prev) => ({ ...prev, password: undefined }));
                }
              }}
              error={!!formErrors.password}
              helperText={formErrors.password}
              margin="normal"
              autoComplete="current-password"
              disabled={submitting}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="パスワードの表示切替"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      disabled={submitting}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={submitting}
              sx={{ mt: 3 }}
            >
              {submitting ? 'ログイン中...' : 'ログイン'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Open-source license notice (MIT) — below the card, not overlapping */}
      <Box sx={{ mt: 4, textAlign: 'center', maxWidth: 520, px: 2 }}>
        <Typography
          variant="caption"
          component="span"
          sx={{
            color: 'grey.500',
            fontSize: '10px',
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
          onClick={() => setLicenseExpanded((prev) => !prev)}
        >
          This software includes Jspreadsheet CE (MIT License) {licenseExpanded ? '▲' : '▼'}
        </Typography>
        <Collapse in={licenseExpanded}>
          <Typography
            variant="caption"
            component="div"
            sx={{
              color: 'grey.500',
              fontSize: '9px',
              lineHeight: 1.4,
              mt: 1,
              whiteSpace: 'pre-line',
            }}
          >
            {'Copyright (c) 2023 Jspreadsheet CE\n\n' +
              'Permission is hereby granted, free of charge, to any person obtaining a copy ' +
              'of this software and associated documentation files (the "Software"), to deal ' +
              'in the Software without restriction, including without limitation the rights ' +
              'to use, copy, modify, merge, publish, distribute, sublicense, and/or sell ' +
              'copies of the Software, and to permit persons to whom the Software is ' +
              'furnished to do so, subject to the following conditions:\n\n' +
              'The above copyright notice and this permission notice shall be included in all ' +
              'copies or substantial portions of the Software.\n\n' +
              'THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR ' +
              'IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, ' +
              'FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE ' +
              'AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER ' +
              'LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, ' +
              'OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE ' +
              'SOFTWARE.'}
          </Typography>
        </Collapse>
      </Box>
    </Box>
  );
};

export default LoginPage;
